import { RouteObject } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import {
  NewsWidget,
  WidgetHost,
  NewsWidgetFactory,
  WidgetMenuEntry,
  routeMatches,
} from '@/core/widgets/export';
import Podcast from '@/modules/podcasts/components';
import { faPodcast } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const urlPath = '/podcasts/:podcastSlug';

enum SlugToId {
  'tech-analysis' = 'HLLC4546099276',
  'fed-speak' = 'HLLC8487778275',
}

enum SlugToTitle {
  'tech-analysis' = 'Tech Analysis',
  'fed-speak' = 'Fed Speak',
}

export class PodcastWidgetFactory implements NewsWidgetFactory {
  get path(): string {
    return urlPath;
  }

  get headerPanel(): string | JSX.Element {
    return (
      <>
        <FontAwesomeIcon icon={faPodcast} /> Podcasts
      </>
    );
  }

  matchesTemplate(s: string): boolean {
    return !!routeMatches(urlPath, s);
  }

  createInstance(s: string): NewsWidget {
    const match = routeMatches(urlPath, s);

    if (match) return new PodcastWidget(match.podcastSlug);

    throw new Error('invalid url');
  }

  getListableUrls(): WidgetMenuEntry[] {
    return [
      {
        url: '/podcasts/tech-analysis',
        name: 'Tech Analysis',
      },
      {
        url: '/podcasts/fed-speak',
        name: 'Fed Speak',
      },
    ];
  }
}

export class PodcastWidget implements NewsWidget {
  defaultTitle = 'Podcast';
  constructor(private podcast: string) {}

  async hasPermission(permissions: string[]) {
    return true;
  }

  get headerPanel(): JSX.Element {
    return (
      <>
        <FontAwesomeIcon icon={faPodcast} /> {SlugToTitle[this.podcast as keyof typeof SlugToTitle]}
      </>
    );
  }

  headerActions({ children }: { children?: React.ReactNode }): JSX.Element {
    return <>{children}</>;
  }

  get id(): string {
    return '/podcasts/' + this.podcast;
  }

  initialize(host: WidgetHost): void {}

  get shortTitle(): JSX.Element {
    return (
      <>
        <FontAwesomeIcon icon={faPodcast} /> {this.podcast}
      </>
    );
  }

  get routes(): RouteObject[] {
    return [
      {
        path: '/',
        element: <PodcastWrapper podcastId={SlugToId[this.podcast as keyof typeof SlugToId]} />,
      },
    ];
  }

  get defaultRoute(): string {
    return `/`;
  }
}

function isLightTheme(root: HTMLElement): boolean {
  return root.getAttribute('data-bs-theme') !== 'dark';
}

export function PodcastWrapper({ podcastId }: { podcastId: string }) {
  const [light, setLight] = useState<boolean | undefined>(() => {
    const root = document.getElementById('html-root-element');
    return root ? isLightTheme(root) : undefined;
  });
  const [loading, setLoading] = useState(false);
  const lightRef = useRef(light);

  useEffect(() => {
    const root = document.getElementById('html-root-element');
    if (!root) return;

    const observer = new MutationObserver(() => {
      const next = isLightTheme(root);
      if (next === lightRef.current) return;
      lightRef.current = next;
      setLoading(true);
      setLight(next);
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-bs-theme'] });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="position-relative">
      <Podcast podcastId={podcastId} light={light} onLoad={() => setLoading(false)} />
      {loading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'var(--bs-body-bg)' }}
        >
          <div className="spinner-border spinner-border-sm text-secondary" role="status" />
        </div>
      )}
    </div>
  );
}
